import '@google/model-viewer';

import { globalCss, styled } from '@f8n-frontend/stitches';
import { useRef, useEffect, useState } from 'react';

import SpinnerStroked from 'components/SpinnerStroked';
import Box from 'components/base/Box';
import Text from 'components/base/Text';

import ARIcon from 'assets/icons/ar-icon.svg';

import { UnsafeAny } from 'types/utils';

interface ModelViewerProps {
  src: string;
  disableAR?: boolean;
  toBlob?: (posterBlob: Blob) => void;
}

const modelViewerStyles = globalCss({
  'model-viewer': {
    '--poster-color': 'transparent',
    width: '100%',
    height: '100%',
  },
  'model-viewer::part(default-progress-mask)': {
    display: 'none',
  },
});

const ARButton = styled(Box, {
  display: 'none',
  alignItems: 'center',
  position: 'absolute',
  bottom: 18,
  left: 18,
  appearance: 'none',
  backgroundColor: '$black50',
  border: 'none',
  borderRadius: 999,
  paddingLeft: '$3',
  paddingRight: '$3',
  paddingTop: '$2',
  paddingBottom: '$2',
  cursor: 'pointer',
  outline: 'none',

  '@bp2': {
    bottom: 25,
    left: 25,
  },
  variants: {
    isLoaded: {
      true: {
        display: 'flex',
      },
    },
  },
});

const ARLabel = styled(Text, {
  marginLeft: '$2',
  color: '$white100',
});

ARLabel.defaultProps = {
  size: 2,
  weight: 'semibold',
};

const LoadingSpinner = styled(Box, {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  opacity: 0.5,
  color: '$black100',
  variants: {
    isLoaded: { true: { display: 'none' } },
  },
});

export default function ModelViewer(props: ModelViewerProps) {
  const { src, toBlob, disableAR, ...rest } = props;
  modelViewerStyles();
  const modelRef = useRef<UnsafeAny>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const model = modelRef?.current;

    if (!model) return;

    const getBlob = async () => {
      if (toBlob) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        const blob = await model.toBlob({ idealAspect: true });
        toBlob(blob);
      }
    };

    const setLoadingState = () => setIsLoaded(true);

    model.addEventListener('load', setLoadingState);

    if (isLoaded) {
      getBlob();
    }

    return () => {
      model.removeEventListener('load', setLoadingState);
    };
  }, [modelRef, toBlob, isLoaded]);

  return (
    <model-viewer
      ref={modelRef}
      src={src}
      autoplay
      auto-rotate
      camera-controls
      {...(disableAR ? null : { ar: true })}
      ar-modes="scene-viewer quick-look"
      interaction-prompt="none"
      data-js-focus-visible
      loading="eager"
      {...rest}
    >
      <span slot="interaction-prompt" />
      <LoadingSpinner slot="progress-bar" isLoaded={isLoaded}>
        <SpinnerStroked size={40} />
      </LoadingSpinner>

      <ARButton slot="ar-button" isLoaded={isLoaded}>
        <Box css={{ width: 18 }}>
          <ARIcon />
        </Box>
        <ARLabel>View in AR</ARLabel>
      </ARButton>
    </model-viewer>
  );
}
